// TASK-042 AC #3 — UI form for the per-rule threshold. Validates against
// the per-type bounds (HEALTH_DROP 0-100, MEMBER_DROP 0-100% i.e. 0-1, and
// ACTIVITY_SPIKE 1-10×) BEFORE invoking the mutation so a malformed value
// never reaches the network.

import { useEffect, useRef, useState } from "react";

import {
  ALERT_THRESHOLD_RANGES,
  validateThresholdForType,
  type AlertRuleResponse,
  type ThresholdValidationError,
} from "@community-analytics/shared";

import {
  formValueToThreshold,
  thresholdInputUnitSuffix,
  thresholdToFormValue,
  useUpdateAlertRuleMutation,
} from "@entities/alert-rule";

import { Button } from "@shared/ui";

export interface AlertThresholdInputProps {
  communityId: string | null;
  rule: AlertRuleResponse;
}

function useLatest<T>(value: T): { current: T } {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

const ERROR_MESSAGES: Record<ThresholdValidationError, string> = {
  threshold_not_finite: "Enter a number.",
  threshold_below_min: "Below the minimum allowed value.",
  threshold_above_max: "Above the maximum allowed value.",
};

export function AlertThresholdInput({
  communityId,
  rule,
}: AlertThresholdInputProps) {
  const update = useUpdateAlertRuleMutation(communityId);
  const initial = thresholdToFormValue(rule.type, rule.threshold);
  const [value, setValue] = useState<string>(String(initial));
  const [error, setError] = useState<ThresholdValidationError | null>(null);

  // Re-sync the input when the upstream rule changes (e.g. server pushed
  // a fresh value via mutation success). The latest-value ref keeps the
  // typed-into-input keystrokes from re-running the effect mid-edit.
  const latestRef = useLatest(value);
  useEffect(() => {
    const incoming = thresholdToFormValue(rule.type, rule.threshold);
    if (Number(latestRef.current) !== incoming) {
      setValue(String(incoming));
      setError(null);
    }
  }, [rule.threshold, rule.type, latestRef]);

  const submit = () => {
    const numeric = Number(value);
    const fraction = formValueToThreshold(rule.type, numeric);
    const validationError = validateThresholdForType(rule.type, fraction);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    update.mutate({
      ruleId: rule.id,
      patch: { threshold: fraction },
    });
  };

  const range = ALERT_THRESHOLD_RANGES[rule.type];
  // Display range bounds in the same UI unit as the input (i.e. 100 for
  // MEMBER_DROP, not 1.0).
  const minDisplay = thresholdToFormValue(rule.type, range.min);
  const maxDisplay = thresholdToFormValue(rule.type, range.max);
  const disabled = communityId === null || update.isPending;

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode="decimal"
        className="h-9 w-24 rounded-md border bg-background px-2 text-sm tabular-nums"
        value={value}
        min={minDisplay}
        max={maxDisplay}
        step={rule.type === "ACTIVITY_SPIKE" ? "0.1" : "1"}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        aria-invalid={error !== null}
        aria-label={`Threshold for ${rule.type}`}
      />
      <span className="text-xs text-muted-foreground">
        {thresholdInputUnitSuffix(rule.type)}
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={disabled}
        onClick={submit}
      >
        Save
      </Button>
      {error ? (
        <span role="alert" className="text-xs text-destructive">
          {ERROR_MESSAGES[error]}
        </span>
      ) : null}
    </div>
  );
}
